import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getInviteByToken, acceptBusinessInvite, declineBusinessInvite, BusinessInviteInterface } from '@app/services/business';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '@app/redux/store';
import { getUserWithBusinesses } from '@app/services/user';
import { ActionTypes } from '@app/redux/types';
import { selectBusiness } from '@app/redux/features/business';
import { Button } from '@app/components/Button/Button';
import EchoLogo from '@app/assets/horizontal-logo.svg?react';

export const AcceptInvite = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const isAuthenticated = !!user;
  const [error, setError] = useState<string | null>(null);

  const {
    data: invite,
    isLoading: isLoadingInvite,
    error: inviteError,
  } = useQuery<BusinessInviteInterface>({
    queryKey: ['invite', token],
    queryFn: () => getInviteByToken(token!),
    enabled: !!token,
    retry: false,
  });

  const acceptMutation = useMutation({
    mutationFn: () => acceptBusinessInvite(token!),
    onSuccess: async (data) => {
      const businessId = data.member.businessId;

      localStorage.removeItem('pendingInviteToken');
      localStorage.removeItem('pendingInviteEmail');

      try {
        const res = await getUserWithBusinesses();
        dispatch({ type: ActionTypes.AUTH_SAVE, payload: res?.data?.user });
        dispatch({ type: ActionTypes.BUSINESS_LOAD_LIST, payload: res?.data?.businesses });

          dispatch(selectBusiness({ id: businessId }));

        navigate('/', { replace: true });
      } catch {
        navigate('/select-business', { replace: true });
      }
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to accept invitation');
    },
  });

  useEffect(() => {
    // If not authenticated, store the token and redirect to sign-up
    if (!isAuthenticated && invite && !isLoadingInvite) {
      localStorage.setItem('pendingInviteToken', token!);
      // Store the invite email to pre-fill the signup form
      if (invite.email) {
        localStorage.setItem('pendingInviteEmail', invite.email);
      }
      navigate('/sign-up', { replace: true });
    }
  }, [isAuthenticated, invite, isLoadingInvite, token, navigate]);

  const handleAccept = () => {
    acceptMutation.mutate();
  };

  const declineMutation = useMutation({
    mutationFn: () => declineBusinessInvite(token!),
    onSuccess: () => {
      localStorage.removeItem('pendingInviteToken');
      localStorage.removeItem('pendingInviteEmail');
      navigate('/select-business', { replace: true });
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to decline invitation');
    },
  });

  const handleDecline = () => {
    declineMutation.mutate();
  };

  if (isLoadingInvite) {
    return (
      <div className="min-h-screen bg-base-ghostWhite flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-baseBlue mx-auto mb-4"></div>
          <p className="text-base-coolGray">Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (inviteError || !invite) {
    return (
      <div className="min-h-screen bg-base-ghostWhite flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <EchoLogo className="h-8 w-auto mx-auto mb-6" />
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-base-raisinBlack mb-2">Invalid Invitation</h1>
          <p className="text-base-coolGray mb-6">
            This invitation link is invalid or has expired. Please contact the person who invited you for a new invitation.
          </p>
          <Button className="mx-auto" onClick={() => navigate('/login')}>Go to Login</Button>
        </div>
      </div>
    );
  }

  if (invite.status !== 'PENDING') {
    return (
      <div className="min-h-screen bg-base-ghostWhite flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <EchoLogo className="h-8 w-auto mx-auto mb-6" />
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-base-raisinBlack mb-2">
            Invitation {invite.status === 'ACCEPTED' ? 'Already Accepted' : invite.status === 'EXPIRED' ? 'Expired' : 'Cancelled'}
          </h1>
          <p className="text-base-coolGray mb-6">
            {invite.status === 'ACCEPTED'
              ? 'This invitation has already been accepted.'
              : invite.status === 'EXPIRED'
              ? 'This invitation has expired. Please request a new invitation.'
              : 'This invitation has been cancelled.'}
          </p>
          <Button className="mx-auto" onClick={() => navigate('/select-business')}>Go to Dashboard</Button>
        </div>
      </div>
    );
  }

  // If not authenticated, this won't render because of the useEffect redirect
  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-base-ghostWhite flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8">
        <EchoLogo className="h-8 w-auto mx-auto mb-6" />

        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-brand-baseBlue/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-brand-baseBlue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-base-raisinBlack mb-2">You're Invited!</h1>
          <p className="text-base-coolGray">
            You've been invited to join <span className="font-semibold text-base-raisinBlack">{invite.business?.name}</span> on Echo Ratings.
          </p>
        </div>

        {invite.jobTitle && (
          <div className="bg-base-ghostWhite rounded-lg p-4 mb-6">
            <p className="text-sm text-base-coolGray">Role</p>
            <p className="font-medium text-base-raisinBlack">{invite.jobTitle}</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 text-red-600 rounded-lg p-4 mb-6 text-sm">
            {error}
          </div>
        )}

        <div className="space-y-3">
          <Button
            onClick={handleAccept}
            disabled={acceptMutation.isPending || declineMutation.isPending}
            className="w-full"
          >
            {acceptMutation.isPending ? 'Accepting...' : 'Accept Invitation'}
          </Button>
          <button
            onClick={handleDecline}
            disabled={acceptMutation.isPending || declineMutation.isPending}
            className="w-full px-6 py-3 bg-base-ghostWhite text-base-raisinBlack font-medium rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            {declineMutation.isPending ? 'Declining...' : 'Decline'}
          </button>
        </div>

        <p className="text-xs text-base-coolGray text-center mt-6">
          By accepting, you'll be added as a team member with the permissions set by the inviter.
        </p>
      </div>
    </div>
  );
};
